import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Chip,
    Divider,
    IconButton,
    Link as MuiLink,
    Menu,
    MenuItem,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    getPrivateClubsByTemplate,
    getPrivateClubTemplateMatches,
    getPrivateClubTemplatePlacedBets,
    getPrivateClubTemplates,
    overridePrivateClubTemplatePlacedBetStatus,
} from '../Api';
import { PrivateClubBetResultState } from '../models/dtos/PrivateClubDto';
import {
    PrivateClubTemplateAdminDto,
    PrivateClubTemplateMatchOptionAdminDto,
    PrivateClubTemplatePlacedBetAdminDto,
    PrivateClubTemplateBetAdminDto,
    PrivateClubTemplatePlacedBetsAdminView,
} from '../models/dtos/PrivateClubAdminDto';
import PrivateClubMatchSummary from '../components/PrivateClubMatchSummary';

const OVERRIDE_STATUSES: PrivateClubBetResultState[] = ['PENDING', 'WON', 'LOST', 'RETURNED'];
const FILTER_OPTIONS_PAGE_SIZE = 50;

interface ClubFilterOption {
    id: string;
    name: string;
}

type OverrideMenuTarget =
    | { scope: 'BET'; row: PrivateClubTemplatePlacedBetAdminDto }
    | { scope: 'GROUP'; row: PrivateClubTemplateBetAdminDto };

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function formatNumber(value?: number | null) {
    return typeof value === 'number' ? value.toLocaleString() : '-';
}

function userName(row: PrivateClubTemplatePlacedBetAdminDto) {
    return row.user?.name?.trim() || 'Unknown user';
}

function matchLabel(row: { matchId: number; match?: PrivateClubTemplatePlacedBetAdminDto['match'] }) {
    const match = row.match as (PrivateClubTemplatePlacedBetAdminDto['match'] & {
        homeTeam?: { name?: string | null } | null;
        awayTeam?: { name?: string | null } | null;
    }) | null | undefined;
    const home = (match?.home_team ?? match?.homeTeam)?.name?.trim();
    const away = (match?.away_team ?? match?.awayTeam)?.name?.trim();
    return home && away ? `${home} vs ${away}` : `Match ${row.matchId}`;
}

function resultColor(status: PrivateClubBetResultState): 'success' | 'error' | 'warning' | 'info' | 'default' {
    if (status === 'WON') return 'success';
    if (status === 'LOST') return 'error';
    if (status === 'PENDING') return 'warning';
    if (status === 'RETURNED') return 'info';
    return 'default';
}

function betGroupLabel(row: PrivateClubTemplatePlacedBetAdminDto | PrivateClubTemplateBetAdminDto) {
    return row.betGroupName || (row.betGroupId ? `Group ${row.betGroupId}` : '-');
}

function parseStringListValue(value: string) {
    return value.split(',').map(item => item.trim()).filter(Boolean);
}

function parseNumberListValue(value: string) {
    return parseStringListValue(value)
        .map(value => Number(value))
        .filter(value => Number.isFinite(value) && value > 0);
}

function setListParam(next: URLSearchParams, key: string, values: Array<string | number>) {
    if (values.length > 0) next.set(key, values.join(','));
    else next.delete(key);
}

function clubOptionLabel(option: ClubFilterOption) {
    return option.name?.trim() || option.id;
}

function matchOptionLabel(option: PrivateClubTemplateMatchOptionAdminDto) {
    const roundLabel = option.roundIndex > 0 ? `Round ${option.roundIndex}` : 'Round';
    const dateLabel = option.match?.date ? formatDate(option.match.date) : null;
    return `${matchLabel(option)} | ${roundLabel}${dateLabel ? ` | ${dateLabel}` : ''} | ${option.settlementStatus}`;
}

export default function PrivateClubTemplatePlacedBetsPage() {
    const navigate = useNavigate();
    const { templateId = '' } = useParams<{ templateId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [template, setTemplate] = React.useState<PrivateClubTemplateAdminDto | null>(null);
    const [items, setItems] = React.useState<PrivateClubTemplatePlacedBetAdminDto[]>([]);
    const [templateBets, setTemplateBets] = React.useState<PrivateClubTemplateBetAdminDto[]>([]);
    const [clubOptions, setClubOptions] = React.useState<ClubFilterOption[]>([]);
    const [clubOptionsTotal, setClubOptionsTotal] = React.useState(0);
    const [clubOptionsLoading, setClubOptionsLoading] = React.useState(false);
    const [matchOptions, setMatchOptions] = React.useState<PrivateClubTemplateMatchOptionAdminDto[]>([]);
    const [matchOptionsTotal, setMatchOptionsTotal] = React.useState(0);
    const [matchOptionsLoading, setMatchOptionsLoading] = React.useState(false);
    const [view, setView] = React.useState<PrivateClubTemplatePlacedBetsAdminView>('USER');
    const [totalItems, setTotalItems] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const [loading, setLoading] = React.useState(false);
    const [overriding, setOverriding] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(null);
    const [menuTarget, setMenuTarget] = React.useState<OverrideMenuTarget | null>(null);
    const clubIdsParam = searchParams.get('clubIds') ?? '';
    const matchIdsParam = searchParams.get('matchIds') ?? '';
    const selectedClubIds = React.useMemo(() => parseStringListValue(clubIdsParam), [clubIdsParam]);
    const selectedMatchIds = React.useMemo(() => parseNumberListValue(matchIdsParam), [matchIdsParam]);

    const load = React.useCallback(async () => {
        if (!templateId) {
            setError('Template id is required.');
            return;
        }
        setLoading(true);
        setError(null);
        const offset = page * rowsPerPage;
        const [betsResponse, templatesResponse] = await Promise.all([
            getPrivateClubTemplatePlacedBets(templateId, view, offset, rowsPerPage, selectedClubIds, selectedMatchIds),
            getPrivateClubTemplates(),
        ]);
        setItems(betsResponse?.items ?? []);
        setTemplateBets(betsResponse?.templateBets ?? []);
        setTotalItems(betsResponse?.totalItems ?? 0);
        setTemplate(templatesResponse?.find(item => item.id === templateId) ?? null);
        setLoading(false);
        if (!betsResponse) setError('Failed to load placed bets for template.');
        if (!templatesResponse) setError(prev => prev ?? 'Failed to load private club template.');
    }, [page, rowsPerPage, selectedClubIds, selectedMatchIds, templateId, view]);

    React.useEffect(() => {
        load();
    }, [load]);

    const loadClubOptions = React.useCallback(async (offset: number) => {
        if (!templateId) return;
        setClubOptionsLoading(true);
        const response = await getPrivateClubsByTemplate(templateId, offset, FILTER_OPTIONS_PAGE_SIZE);
        setClubOptionsLoading(false);
        if (!response) return;
        setClubOptions(prev => {
            const next = offset === 0 ? [] : [...prev];
            response.items.forEach(club => {
                if (!next.some(option => option.id === club.id)) {
                    next.push({ id: club.id, name: club.name });
                }
            });
            return next;
        });
        setClubOptionsTotal(response.totalItems);
    }, [templateId]);

    const loadMatchOptions = React.useCallback(async (offset: number) => {
        if (!templateId) return;
        setMatchOptionsLoading(true);
        const response = await getPrivateClubTemplateMatches(templateId, offset, FILTER_OPTIONS_PAGE_SIZE);
        setMatchOptionsLoading(false);
        if (!response) return;
        setMatchOptions(prev => {
            const next = offset === 0 ? [] : [...prev];
            response.items.forEach(match => {
                if (!next.some(option => option.matchId === match.matchId)) {
                    next.push(match);
                }
            });
            return next;
        });
        setMatchOptionsTotal(response.totalItems);
    }, [templateId]);

    React.useEffect(() => {
        setClubOptions([]);
        setClubOptionsTotal(0);
        setMatchOptions([]);
        setMatchOptionsTotal(0);
        void loadClubOptions(0);
        void loadMatchOptions(0);
    }, [loadClubOptions, loadMatchOptions]);

    const changeView = (_: React.SyntheticEvent, nextView: PrivateClubTemplatePlacedBetsAdminView) => {
        setView(nextView);
        setPage(0);
    };

    const selectedClubOptions = React.useMemo(() => selectedClubIds.map(clubId =>
        clubOptions.find(option => option.id === clubId) ?? { id: clubId, name: clubId }
    ), [clubOptions, selectedClubIds]);

    const selectedMatchOptions = React.useMemo(() => selectedMatchIds.map(matchId =>
        matchOptions.find(option => option.matchId === matchId) ?? {
            matchId,
            roundId: '',
            roundIndex: 0,
            settlementStatus: 'PENDING' as const,
            match: null,
        }
    ), [matchOptions, selectedMatchIds]);

    const updateFilters = React.useCallback((clubIds: string[], matchIds: number[]) => {
        const next = new URLSearchParams(searchParams);
        setListParam(next, 'clubIds', clubIds);
        setListParam(next, 'matchIds', matchIds);
        setPage(0);
        setSearchParams(next);
    }, [searchParams, setSearchParams]);

    const loadNextClubOptions = React.useCallback(() => {
        if (!clubOptionsLoading && clubOptions.length < clubOptionsTotal) {
            void loadClubOptions(clubOptions.length);
        }
    }, [clubOptions.length, clubOptionsLoading, clubOptionsTotal, loadClubOptions]);

    const loadNextMatchOptions = React.useCallback(() => {
        if (!matchOptionsLoading && matchOptions.length < matchOptionsTotal) {
            void loadMatchOptions(matchOptions.length);
        }
    }, [loadMatchOptions, matchOptions.length, matchOptionsLoading, matchOptionsTotal]);

    const handleClubOptionsScroll = React.useCallback((event: React.UIEvent<HTMLUListElement>) => {
        const node = event.currentTarget;
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - 16) {
            loadNextClubOptions();
        }
    }, [loadNextClubOptions]);

    const handleMatchOptionsScroll = React.useCallback((event: React.UIEvent<HTMLUListElement>) => {
        const node = event.currentTarget;
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - 16) {
            loadNextMatchOptions();
        }
    }, [loadNextMatchOptions]);

    const openOverrideMenu = (event: React.MouseEvent<HTMLElement>, target: OverrideMenuTarget) => {
        event.preventDefault();
        setMenuAnchorEl(event.currentTarget);
        setMenuTarget(target);
    };

    const closeOverrideMenu = () => {
        setMenuAnchorEl(null);
        setMenuTarget(null);
    };

    const overrideStatus = async (status: PrivateClubBetResultState, clearOverride = false) => {
        if (!templateId || !menuTarget || overriding) return;
        setOverriding(true);
        setError(null);
        const response = menuTarget.scope === 'BET'
            ? await overridePrivateClubTemplatePlacedBetStatus(templateId, {
                scope: 'BET',
                status,
                clearOverride,
                privateClubTemplateUserBetId: menuTarget.row.privateClubTemplateUserBetId,
            })
            : await overridePrivateClubTemplatePlacedBetStatus(templateId, {
                scope: 'GROUP',
                status,
                clearOverride,
                templateBetId: menuTarget.row.templateBetId,
                matchId: menuTarget.row.matchId,
                betTypeId: menuTarget.row.betTypeId,
                betTypeParam: menuTarget.row.betTypeParam,
            });
        closeOverrideMenu();
        setOverriding(false);
        if (!response) {
            setError('Failed to override placed bet status.');
            return;
        }
        await load();
    };

    const statusCell = (status: PrivateClubBetResultState, overriddenText?: string | null) => (
        <Stack spacing={0.25}>
            <Chip size="small" label={status} color={resultColor(status)} />
            {overriddenText && (
                <Typography variant="caption" color="warning.main" sx={{ lineHeight: 1 }}>
                    {overriddenText}
                </Typography>
            )}
        </Stack>
    );

    const templateBetStatusCell = (row: PrivateClubTemplateBetAdminDto) =>
        statusCell(row.resultState, row.statusOverridden ? 'overridden' : null);

    return (
        <Stack gap={2} sx={{ p: 2 }}>
            <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Stack gap={0.5}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/private-clubs')} sx={{ alignSelf: 'flex-start' }}>
                        Back to templates
                    </Button>
                    <Typography variant="h5">Private club bets</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Template: {template?.title ?? templateId}
                    </Typography>
                    {template && (
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Chip size="small" label={template.status} color={template.status === 'ACTIVE' ? 'success' : 'default'} />
                            <Chip size="small" variant="outlined" label={`${template.clubsCount} clubs`} />
                            <Chip size="small" variant="outlined" label={`${template.placedBetsCount} bets`} />
                        </Stack>
                    )}
                </Stack>
                <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Stack>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                <Autocomplete
                    multiple
                    size="small"
                    options={clubOptions}
                    value={selectedClubOptions}
                    getOptionLabel={clubOptionLabel}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    loading={clubOptionsLoading}
                    ListboxProps={{ onScroll: handleClubOptionsScroll }}
                    onOpen={() => {
                        if (clubOptions.length === 0) void loadClubOptions(0);
                    }}
                    onChange={(_, value) => updateFilters(value.map(option => option.id), selectedMatchIds)}
                    renderInput={params => <TextField {...params} label="Clubs" placeholder="All clubs" />}
                    sx={{ minWidth: { xs: '100%', md: 320 } }}
                />
                <Autocomplete
                    multiple
                    size="small"
                    options={matchOptions}
                    value={selectedMatchOptions}
                    getOptionLabel={matchOptionLabel}
                    isOptionEqualToValue={(option, value) => option.matchId === value.matchId}
                    loading={matchOptionsLoading}
                    ListboxProps={{ onScroll: handleMatchOptionsScroll }}
                    onOpen={() => {
                        if (matchOptions.length === 0) void loadMatchOptions(0);
                    }}
                    filterOptions={(options, state) => {
                        const query = state.inputValue.trim().toLowerCase();
                        if (!query) return options;
                        return options.filter(option =>
                            matchOptionLabel(option).toLowerCase().includes(query) ||
                            String(option.matchId).includes(query)
                        );
                    }}
                    onChange={(_, value) => updateFilters(selectedClubIds, value.map(option => option.matchId))}
                    renderInput={params => <TextField {...params} label="Matches" placeholder="All matches" />}
                    sx={{ minWidth: { xs: '100%', md: 420 } }}
                />
            </Stack>

            <Tabs value={view} onChange={changeView}>
                <Tab value="USER" label="User specific" />
                <Tab value="GROUPED" label="Template bets" />
            </Tabs>

            {view === 'USER' ? (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Club</TableCell>
                            <TableCell>Round</TableCell>
                            <TableCell>Match</TableCell>
                            <TableCell>Bet group</TableCell>
                            <TableCell>Bet</TableCell>
                            <TableCell>Odds</TableCell>
                            <TableCell>Expected pts</TableCell>
                            <TableCell>Received pts</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Placed</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={12} align="center">Loading placed bets...</TableCell>
                            </TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={12} align="center">No placed bets found for this template</TableCell>
                            </TableRow>
                        )}
                        {!loading && items.map(row => (
                            <TableRow
                                key={row.privateClubTemplateUserBetId}
                                onContextMenu={event => openOverrideMenu(event, { scope: 'BET', row })}
                            >
                                <TableCell>
                                    <MuiLink href={`/users/${row.userId}`} target="_blank" rel="noreferrer" underline="hover">
                                        {userName(row)}
                                    </MuiLink>
                                </TableCell>
                                <TableCell>
                                    <Stack spacing={0.75}>
                                        {row.clubs.map(club => (
                                            <Stack key={club.clubId} spacing={0.25}>
                                                <MuiLink href={`/users/${row.userId}/private-clubs/${club.clubId}`} target="_blank" rel="noreferrer" underline="hover">
                                                    {club.clubName}
                                                </MuiLink>
                                                <Typography variant="caption" color="text.secondary">{club.clubId}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </TableCell>
                                <TableCell>{row.roundId}</TableCell>
                                <TableCell>
                                    <PrivateClubMatchSummary
                                        matchId={row.matchId}
                                        match={row.match}
                                        link
                                        newTab
                                    />
                                </TableCell>
                                <TableCell>{betGroupLabel(row)}</TableCell>
                                <TableCell>{row.betName}</TableCell>
                                <TableCell>{formatNumber(row.oddsSnapshot)}</TableCell>
                                <TableCell>{formatNumber(row.scorePointsExpected)}</TableCell>
                                <TableCell>{formatNumber(row.scorePointsReceived)}</TableCell>
                                <TableCell>{statusCell(row.resultState, row.statusOverridden ? 'overridden' : null)}</TableCell>
                                <TableCell>{formatDate(row.createdAt)}</TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={event => openOverrideMenu(event, { scope: 'BET', row })}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Round</TableCell>
                            <TableCell>Match</TableCell>
                            <TableCell>Bet group</TableCell>
                            <TableCell>Bet</TableCell>
                            <TableCell>Core odds</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Placements (all clubs)</TableCell>
                            <TableCell>Calculated</TableCell>
                            <TableCell>First created</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={10} align="center">Loading template bets...</TableCell>
                            </TableRow>
                        )}
                        {!loading && templateBets.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={10} align="center">No template bets found</TableCell>
                            </TableRow>
                        )}
                        {!loading && templateBets.map(row => (
                            <TableRow
                                key={row.templateBetId}
                                onContextMenu={event => openOverrideMenu(event, { scope: 'GROUP', row })}
                            >
                                <TableCell>{row.roundId}</TableCell>
                                <TableCell>
                                    <PrivateClubMatchSummary
                                        matchId={row.matchId}
                                        match={row.match}
                                        link
                                        newTab
                                    />
                                </TableCell>
                                <TableCell>{betGroupLabel(row)}</TableCell>
                                <TableCell>{row.betName}</TableCell>
                                <TableCell>{formatNumber(row.oddsSnapshot)}</TableCell>
                                <TableCell>{templateBetStatusCell(row)}</TableCell>
                                <TableCell>{formatNumber(row.betsCount)}</TableCell>
                                <TableCell>{formatDate(row.calculatedAt)}</TableCell>
                                <TableCell>{formatDate(row.createdAt)}</TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={event => openOverrideMenu(event, { scope: 'GROUP', row })}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={event => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
            />

            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeOverrideMenu}>
                {OVERRIDE_STATUSES.map(status => (
                    <MenuItem key={status} onClick={() => overrideStatus(status)} disabled={overriding}>
                        Set status to {status}
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem
                    onClick={() => overrideStatus('PENDING', true)}
                    disabled={overriding || !menuTarget?.row.statusOverridden}
                >
                    Remove override and set pending
                </MenuItem>
            </Menu>
        </Stack>
    );
}
